import { FC, useMemo } from 'react';
import { Stack } from '@mui/material';
import Text from '@/components/text';
import ViolationChip from '@/components/chip/violation-chip';
import ClassifierLink from '@/components/classifier-link';
import { IntentScore, RegexSdDetection, SdPosition, Violation } from '@/lib/models/user-event';
import { createUseStyles } from 'react-jss';
import OriginalMessage from './original-message';
import Card from '@/components/card';

interface Props {
  violationsList: Violation[];
  customIntents: IntentScore[];
  originalMessage?: string;
  detections: SdPosition[];
  regexDetections: RegexSdDetection[];
}

const useStyles = createUseStyles({
  card: {
    border: 'none !important',
  },
});

const MessageDetection: FC<Props> = ({
  violationsList,
  customIntents,
  regexDetections,
  originalMessage,
  detections,
}) => {
  const styles = useStyles();
  const showViolations = !!violationsList.length;
  const showIntents = !!customIntents.length;
  const showRegexDetections = !!regexDetections.length;
  const showClassifierDetections = showIntents || showRegexDetections;
  const showOriginal = !!originalMessage?.trim().length;
  const uniqueRegexDetections = useMemo(() => {
    return Array.from(
      new Map(regexDetections.map(({ classifierId, name }) => [classifierId, name])).entries()
    );
  }, [regexDetections]);
  const regexDetectionsPositions = regexDetections.map(({ startPos, endPos, name }) => ({
    startPos,
    endPos,
    type: name,
    score: 1,
  })) as SdPosition[];
  const compDetections = [...(detections || []), ...(regexDetectionsPositions || [])];

  return (
    <Card className={styles.card}>
      <Stack spacing={2}>
        {showViolations && (
          <Stack spacing={1}>
            <Text bold size="small">
              Violations
            </Text>
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {violationsList.map((violation) => (
                <ViolationChip key={violation} violation={violation} size="x-small" />
              ))}
            </Stack>
          </Stack>
        )}
        {showClassifierDetections && (
          <Stack spacing={1}>
            <Text bold size="small">
              Classifier Detections
            </Text>
            {showRegexDetections && (
              <Stack gap={1}>
                {uniqueRegexDetections.map(([classifierId, name]) => (
                  <ClassifierLink id={classifierId} name={name} type="regex" key={classifierId} />
                ))}
              </Stack>
            )}
            {showIntents && (
              <Stack gap={1}>
                {customIntents.map(({ id, name, confidence }) => (
                  <ClassifierLink
                    id={id}
                    name={name}
                    type="intent"
                    key={id}
                    confidence={confidence}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
        {showOriginal && (
          <OriginalMessage originalMessage={originalMessage} detections={compDetections} />
        )}
      </Stack>
    </Card>
  );
};

export default MessageDetection;
